import React from 'react'
import { COLORS } from './theme'
import { isEmbeddable } from './embed'

// What the main view shows when a nav item under "Apps" is selected.
//
// Whether this embeds the app or launches it is not a setting — it follows from
// whether the browser will send the portal's session cookie to the app's host.
// While every tool is on its own *.run.app hostname that cookie is host-only,
// so an iframe would render the app's login screen rather than the app (and
// Google will not let its sign-in page be framed at all). That is a launch
// pane, not an embed, and it says so plainly.
//
// Once the portal and the tools share a parent domain and the session cookie is
// scoped to it, the same check turns embedding on by itself — see embed.js.
// Anything failing that check keeps the launch pane, so a half-migrated estate
// renders honestly app by app rather than all-or-nothing.

const STATUS = {
    ok: { label: 'Responding', color: COLORS.success },
    error: { label: 'Not responding', color: COLORS.danger },
    coming_soon: { label: 'Not deployed yet', color: COLORS.textMuted },
}

// `portalHost` is a prop rather than a direct window.location read so this
// component stays a pure function of its inputs: the embed branch is then
// reachable from the server-rendered smoke tests, which have no window at all.
// A branch no test can enter is the coverage hole this repo has been bitten by
// before.
export default function AppView({ app, health, sessionCookieDomain = null, portalHost = null }) {
    if (!app) {
        return (
            <div style={{ padding: 48, color: COLORS.textMuted }}>
                That app is not in your list. It may have been removed, or your role may no
                longer include it.
            </div>
        )
    }

    const status = STATUS[health] || { label: 'Checking…', color: COLORS.textMuted }
    const launchable = !app.comingSoon && app.url && health !== 'coming_soon'

    // Only frame an app the session cookie actually reaches, and only when the
    // app is actually up. `launchable` alone is not that check — it excludes
    // 'coming_soon' but NOT 'error', which is what /api/apps/health reports for
    // a tool that is down, so relying on it framed Cloud Run's 502 page in a
    // chromeless pane under a red "Not responding" badge.
    //
    // `undefined` stays embeddable on purpose: it is the in-flight state before
    // health resolves, and excluding it would flash the launch pane on every
    // navigation.
    const embedded =
        launchable &&
        health !== 'error' &&
        isEmbeddable({
            appUrl: app.url,
            portalHost,
            cookieDomain: sessionCookieDomain,
        })

    if (embedded) {
        // height:100% of the scroll region, which has a definite height, so the
        // frame fills the pane exactly and the region never scrolls on an
        // embedded app. The pane is a block box, so this cannot be a flex-basis
        // — see the note in App.jsx.
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '14px 24px',
                        borderBottom: `1px solid ${COLORS.border}`,
                        flex: '0 0 auto',
                    }}
                >
                    <h2 style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, margin: 0 }}>
                        {app.name}
                    </h2>
                    <span
                        style={{
                            fontSize: 12,
                            color: status.color,
                            border: `1px solid ${status.color}55`,
                            borderRadius: 999,
                            padding: '2px 10px',
                        }}
                    >
                        {status.label}
                    </span>
                    {/* An embedded app has no address bar, so the way out has to be
                        in the chrome — otherwise a tool that wants a real window
                        (printing, a popup, deep-linking) is a dead end. */}
                    <a
                        href={app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            marginLeft: 'auto',
                            fontSize: 13,
                            color: COLORS.textMuted,
                            textDecoration: 'none',
                        }}
                    >
                        Open in new tab ↗
                    </a>
                </div>
                {/* key={app.id} so switching apps REMOUNTS the frame rather than
                    navigating the existing one. React would otherwise reuse the
                    node and just swap src, which pushes onto the joint session
                    history: the browser Back button then walks the iframe
                    through previously-viewed tools while the portal's nav and
                    heading stay put, and nothing resyncs them because an iframe
                    navigation fires no hashchange.

                    sandbox keeps allow-same-origin, so the frame runs on its
                    real origin and its own cookies and SSO work exactly as they
                    do unframed. What it removes is the one capability a framed
                    tool has over the portal page with no legitimate use for it:
                    allow-top-navigation, i.e. redirecting the whole window. It
                    does NOT stop a sibling setting a cookie on the shared
                    parent domain — that is a property of sharing the domain at
                    all, framed or not, and is why login rotates the session id
                    in @bstock/auth. */}
                <iframe
                    key={app.id}
                    src={app.url}
                    title={app.name}
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals"
                    style={{ flex: '1 1 auto', width: '100%', border: 'none', minHeight: 0 }}
                />
            </div>
        )
    }

    return (
        <div style={{ maxWidth: 780, padding: '40px 40px 64px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, color: COLORS.text, margin: 0 }}>
                    {app.name}
                </h2>
                <span
                    style={{
                        fontSize: 12,
                        color: status.color,
                        border: `1px solid ${status.color}55`,
                        borderRadius: 999,
                        padding: '2px 10px',
                    }}
                >
                    {status.label}
                </span>
            </div>

            {app.description && (
                <p style={{ color: COLORS.textMuted, fontSize: 15, lineHeight: 1.6, marginTop: 0 }}>
                    {app.description}
                </p>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 28 }}>
                {launchable ? (
                    <a
                        href={app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'inline-block',
                            background: COLORS.primary,
                            color: '#fff',
                            padding: '10px 20px',
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: 600,
                            textDecoration: 'none',
                        }}
                    >
                        Open {app.name} ↗
                    </a>
                ) : (
                    <span style={{ color: COLORS.textMuted, fontSize: 14 }}>
                        No URL registered for this app yet.
                    </span>
                )}
            </div>

            {app.url && (
                <div style={{ marginTop: 20, fontSize: 12, color: COLORS.textMuted }}>
                    <code style={{ color: COLORS.textMuted }}>{app.url}</code>
                </div>
            )}

            {launchable && (
                <p style={{ marginTop: 32, fontSize: 13, color: COLORS.textMuted, lineHeight: 1.6 }}>
                    Opening this app signs you in separately for now.
                </p>
            )}
        </div>
    )
}
